import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  CalendarClock,
  Plus, 
  Trash2, 
  Users, 
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  X,
  Save,
  XCircle,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

import { 
  API, 
  dayTypeLabels, 
  dayTypeColors,
  roleLabels 
} from './mesaiConstants';
import { getRoleTitle, sortDates, getPlanStatusInfo } from './mesaiHelpers';
import DraggableModal from './DraggableModal';
import PlanDetailContent from './PlanDetailContent';
import { useRoleFlags } from '@/auth/useRoleFlags';

const TaslakPlanlarSection = ({ userRole, personnel, parks, holidays, planFromSurvey, onPlanFromSurveyHandled, onPlanCreated }) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [draftPlans, setDraftPlans] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [surveyResponses, setSurveyResponses] = useState([]); // Seçilen anketin cevapları
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] = useState(false); // Kaydetme onayı
  const [editingPlan, setEditingPlan] = useState(null); // Düzenleme modu
  
  // Anket sonuçlarından gelen bilgiyi sakla
  const [surveyIdForPlan, setSurveyIdForPlan] = useState(null);
  
  // Silme onay diyalogu
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  
  // Create Plan Form
  const [planTitle, setPlanTitle] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [dayType, setDayType] = useState('cumartesi');
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [assignments, setAssignments] = useState([]); // [{park_id, park_name, personnel: [...]}]
  const [rejectionReason, setRejectionReason] = useState('');
  const assignmentsScrollRef = useRef(null);
  const prevAssignmentsLengthRef = useRef(0);

  // Yetki kontrolü
  const canCreatePlan = ['formen', 'bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(userRole);
  const canApprovePlan = ['bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(userRole);
  const isAutoApproved = ['mudur_yardimcisi', 'mudur'].includes(userRole);
  const canDeletePlan = ['mudur', 'mudur_yardimcisi'].includes(userRole);

  // Mevcut kullanıcının bilgilerini al
  const getCurrentUser = () => {
    const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
    return {
      id: auth.personnelId,
      name: auth.personnelName,
      role: auth.userRole
    };
  };

  // Planı görüntüle ve görüldü olarak işaretle
  const handleViewPlan = async (plan) => {
    setSelectedPlan(plan);
    setShowDetailDialog(true);
    
    // Planı görüntülendi olarak işaretle
    const currentUser = getCurrentUser();
    if (currentUser.id) {
      try {
        await axios.put(`${API}/api/overtime-draft-plans/${plan.id}/mark-viewed?personnel_id=${currentUser.id}`);
      } catch (e) {
        console.log('Mark plan viewed error:', e);
      }
    }
  };

  // Kullanıcının erişebileceği parkları filtrele
  const getFilteredParks = () => {
    const currentUser = getCurrentUser();
    const currentPerson = personnel?.find(p => p.id === currentUser.id);
    const myRegionIds = currentPerson?.region_ids || [];
    
    // Üst amirler tüm parkları görebilir
    if (['mudur', 'mudur_yardimcisi', 'bolge_muhendisi'].includes(userRole)) {
      return parks;
    }
    
    // Formen sadece kendi bölgesindeki parkları görebilir
    if (myRegionIds.length > 0) {
      return parks.filter(park => myRegionIds.includes(park.region_id));
    }
    
    return parks;
  };

  // Kullanıcının erişebileceği personelleri filtrele - Kendisine bağlı personeller alfabetik sıralı
  const getFilteredPersonnel = () => {
    const currentUser = getCurrentUser();
    const people = Array.isArray(personnel) ? personnel : [];
    const currentPerson = people.find(p => p.id === currentUser.id);
    
    let filteredList = [];
    
    if (isMudur) {
      // Müdür: Tüm personel (formen ve BM hariç - onlar park ataması yapılmaz)
      filteredList = people.filter(p => 
        p.role !== 'formen' && p.role !== 'bolge_muhendisi' && p.role !== 'mudur'
      );
    } else if (isMudurYardimcisi) {
      // Müdür Yardımcısı: Sadece kendisine bağlı personel (formen ve BM hariç)
      filteredList = people.filter(p => {
        if (p.role === 'formen' || p.role === 'bolge_muhendisi' || p.role === 'mudur' || p.role === 'mudur_yardimcisi') return false;
        
        // Direkt bağlı personel
        if (p.mudur_yardimcisi_id === currentUser.id) return true;
        
        // Formen aracılığıyla bağlı personel
        const formen = people.find(f => f.id === p.formen_id);
        if (formen?.mudur_yardimcisi_id === currentUser.id) return true;
        
        // BM aracılığıyla bağlı personel
        const bm = people.find(b => b.id === p.bolge_muhendisi_id);
        if (bm?.mudur_yardimcisi_id === currentUser.id) return true;
        
        return false;
      });
    } else if (isBolgeMuhendisi) {
      // BM: Sadece kendisine bağlı personel (formen ve BM hariç)
      filteredList = people.filter(p => {
        if (p.role === 'formen' || p.role === 'bolge_muhendisi' || p.role === 'mudur' || p.role === 'mudur_yardimcisi') return false;
        
        // Direkt bağlı personel
        if (p.bolge_muhendisi_id === currentUser.id) return true;
        
        // Formen aracılığıyla bağlı personel
        const formen = people.find(f => f.id === p.formen_id);
        if (formen?.bolge_muhendisi_id === currentUser.id) return true;
        
        return false;
      });
    } else if (isFormen) {
      // Formen: Kendi altındaki personel (eski verilerde parent_id ile de bağlı olabilir)
      filteredList = people.filter(p =>
        (p.formen_id === currentUser.id || p.parent_id === currentUser.id) &&
        p.role !== 'formen' && p.role !== 'bolge_muhendisi'
      );
    } else {
      filteredList = [];
    }

    // Güvenli fallback: hiyerarşi alanları eksik/eskiyse personel listesi tamamen boş kalmasın.
    if (filteredList.length === 0) {
      filteredList = people.filter(
        p => ['park_gorevlisi'].includes(p.role) && p.status === 'active'
      );
    }
    
    // Alfabetik sırala
    return filteredList.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr'));
  };

  const fetchDraftPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/overtime-draft-plans`);
      const allPlans = response.data;
      
      // Mevcut kullanıcı bilgilerini al
      const currentUser = getCurrentUser();
      const currentPerson = personnel?.find(p => p.id === currentUser.id);
      
      // HİYERARŞİK GÖRÜNÜRLÜK FİLTRESİ
      const visiblePlans = allPlans.filter(plan => {
        const creatorRole = plan.created_by_role;
        const isApproved = plan.status === 'onaylandi';
        
        // ONAYLANMIŞ PLANLARI HERKES GÖREBİLİR
        if (isApproved) {
          // Hedefli plan kontrolü - sadece hedef kişiler görebilir
          if (plan.target_formen_id) {
            if (currentUser.id === plan.target_formen_id) return true;
            if (currentPerson?.formen_id === plan.target_formen_id) return true;
            if (plan.target_bolge_muhendisi_id && currentUser.id === plan.target_bolge_muhendisi_id) return true;
            if (['mudur', 'mudur_yardimcisi'].includes(userRole)) return true;
            return false;
          }
          if (plan.target_bolge_muhendisi_id) {
            if (currentUser.id === plan.target_bolge_muhendisi_id) return true;
            if (currentPerson?.bolge_muhendisi_id === plan.target_bolge_muhendisi_id) return true;
            if (['mudur', 'mudur_yardimcisi'].includes(userRole)) return true;
            return false;
          }
          return true; // Hedef yoksa herkes görebilir
        }
        
        // TASLAK/BEKLEYEN PLANLAR - SADECE İLGİLİ KİŞİLER GÖREBİLİR
        
        // Kendi oluşturduğu planı her zaman görebilir
        if (plan.created_by === currentUser.id) return true;
        
        // MÜDÜR YARDIMCISI OLUŞTURDU → Sadece kendisi görebilir (başkaları göremez)
        if (creatorRole === 'mudur_yardimcisi') {
          // Müdür her zaman görebilir (üst makam)
          if (isMudur) return true;
          return false; // Diğerleri göremez
        }
        
        // MÜDÜR OLUŞTURDU → Sadece kendisi görebilir
        if (creatorRole === 'mudur') {
          return false; // Müdürün planını sadece kendisi görür
        }
        
        // BM OLUŞTURDU → BM + Müdür Yardımcısı görebilir
        if (creatorRole === 'bolge_muhendisi') {
          const creator = personnel?.find(p => p.id === plan.created_by);
          if (creator?.mudur_yardimcisi_id === currentUser.id) return true;
          if (isMudur) return true;
          return false;
        }
        
        // FORMEN OLUŞTURDU → Hiyerarşik görünürlük
        if (creatorRole === 'formen') {
          const creator = personnel?.find(p => p.id === plan.created_by);
          if (creator) {
            // BM varsa BM görebilir
            if (creator.bolge_muhendisi_id === currentUser.id) return true;
            // Müdür Yardımcısı görebilir
            if (creator.mudur_yardimcisi_id === currentUser.id) return true;
            // Müdür görebilir
            if (isMudur) return true;
          }
          return false;
        }
        
        return false;
      });
      
      setDraftPlans(visiblePlans);
    } catch (error) {
      console.error('Draft plans fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [personnel, userRole]);

  const fetchSurveys = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/overtime-surveys?status=closed`);
      setSurveys(response.data);
    } catch (error) {
      console.error('Surveys fetch error:', error);
    }
  }, []);

  // Anket seçildiğinde o anketin cevaplarını getir
  const fetchSurveyResponsesForPlan = useCallback(async (surveyId, targetDate) => {
    if (!surveyId) {
      setSurveyResponses([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/overtime-survey-responses?survey_id=${surveyId}`);
      // Eğer belirli bir tarih varsa, o tarihe cevap verenleri filtrele
      if (targetDate) {
        const filtered = response.data.filter(r => r.available_dates?.includes(targetDate));
        setSurveyResponses(filtered.map(r => ({
          id: r.personnel_id,
          name: r.personnel_name,
          role: r.personnel_role || 'park_gorevlisi'
        })));
      } else {
        setSurveyResponses(response.data.map(r => ({
          id: r.personnel_id,
          name: r.personnel_name,
          role: r.personnel_role || 'park_gorevlisi',
          availableDates: r.available_dates
        })));
      }
    } catch (error) {
      console.error('Survey responses fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchDraftPlans();
    fetchSurveys();
  }, [fetchDraftPlans, fetchSurveys]);

  // SSE'den gelen plan güncellemelerini dinle
  useEffect(() => {
    const handleRefreshDraftPlans = () => {
      console.log('[TaslakPlanlarSection] SSE refresh event received');
      fetchDraftPlans();
    };
    
    window.addEventListener('refreshDraftPlans', handleRefreshDraftPlans);
    return () => window.removeEventListener('refreshDraftPlans', handleRefreshDraftPlans);
  }, [fetchDraftPlans]);

  // Anket sonuçlarından plan oluşturma geldiğinde
  useEffect(() => {
    if (planFromSurvey) {
      // Formu doldur
      setPlanTitle(`${format(parseISO(planFromSurvey.targetDate), 'd MMMM', { locale: tr })} Mesai Planı`);
      setTargetDate(planFromSurvey.targetDate);
      setSelectedSurveyId(planFromSurvey.surveyId);
      
      // Günün türünü belirle (Cumartesi/Pazar)
      const dayOfWeek = parseISO(planFromSurvey.targetDate).getDay();
      if (dayOfWeek === 6) setDayType('cumartesi');
      else if (dayOfWeek === 0) setDayType('pazar');
      else setDayType('resmi_tatil');
      
      // Uygun personeli kaydet
      setSurveyResponses(planFromSurvey.availablePersonnel || []);
      
      // Anket ID'sini sakla (plan kaydedildikten sonra anket sonuçlarına dönmek için)
      setSurveyIdForPlan(planFromSurvey.surveyId);
      
      // Dialog'u aç
      setShowCreateDialog(true);
      
      // Callback'i çağır (tekrar tetiklenmemesi için)
      if (onPlanFromSurveyHandled) {
        onPlanFromSurveyHandled();
      }
    }
  }, [planFromSurvey, onPlanFromSurveyHandled]);

  const handleCreatePlan = async () => {
    if (!planTitle.trim() || !targetDate || assignments.length === 0) {
      toast.error('Lütfen plan başlığı, tarih ve en az bir park ataması yapın');
      return;
    }

    try {
      setLoading(true);
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      
      const planData = {
        survey_id: selectedSurveyId || null,
        title: planTitle,
        target_date: targetDate,
        day_type: dayType,
        assignments: assignments
      };

      try {
        await axios.post(`${API}/api/overtime-draft-plans?created_by=${auth.personnelId}&created_by_name=${encodeURIComponent(auth.personnelName || '')}&created_by_role=${auth.userRole}`
          , planData);
      } catch (createErr) {
        const detail = createErr?.response?.data?.detail || '';
        // Bazı local-mode backend kopyalarında survey lookup hatası verebiliyor.
        // Bu durumda anket bağlantısı olmadan güvenli fallback ile taslağı kaydet.
        if (String(detail).includes("NoneType") && String(detail).includes("overtime_surveys")) {
          const fallbackPlanData = { ...planData, survey_id: null };
          await axios.post(`${API}/api/overtime-draft-plans?created_by=${auth.personnelId}&created_by_name=${encodeURIComponent(auth.personnelName || '')}&created_by_role=${auth.userRole}`
            , fallbackPlanData);
          toast.info('Anket bağlantısı olmadan taslak kaydedildi');
        } else {
          throw createErr;
        }
      }

      toast.success('Taslak plan oluşturuldu');
      setShowCreateDialog(false);
      setPlanTitle('');
      setTargetDate('');
      setAssignments([]);
      fetchDraftPlans();
      
      // Anket sonuçlarından geldiyse callback'i çağır
      if (surveyIdForPlan && onPlanCreated) {
        onPlanCreated(surveyIdForPlan);
        setSurveyIdForPlan(null);
      }
    } catch (error) {
      toast.error('Plan oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async (planId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      await axios.put(`${API}/api/overtime-draft-plans/${planId}/submit-for-approval?submitted_by=${auth.personnelId}&submitted_by_name=${encodeURIComponent(auth.personnelName || '')}&submitted_by_role=${auth.userRole}`);
      toast.success('Plan onaya gönderildi');
      fetchDraftPlans();
    } catch (error) {
      toast.error('Onaya gönderilemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Müdür/Müdür Yardımcısı için direkt onaylama
  const handleDirectApprove = async (planId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const response = await axios.put(`${API}/api/overtime-draft-plans/${planId}/approve?approved_by=${auth.personnelId}&approved_by_name=${encodeURIComponent(auth.personnelName || '')}&approved_by_role=${auth.userRole}`);
      
      toast.success('Plan onaylandı ve mesai çizelgesi oluşturuldu ✓');
      fetchDraftPlans();
    } catch (error) {
      toast.error('Onaylama başarısız: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApprovePlan = async (planId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const response = await axios.put(`${API}/api/overtime-draft-plans/${planId}/approve?approved_by=${auth.personnelId}&approved_by_name=${encodeURIComponent(auth.personnelName || '')}&approved_by_role=${auth.userRole}`);
      
      // Backend'den gelen mesajı göster
      if (response.data.is_final) {
        toast.success('Plan onaylandı ve mesai çizelgesi oluşturuldu ✓');
      } else {
        toast.success(response.data.message || 'Plan onaylandı, üst makama gönderildi');
      }
      
      fetchDraftPlans();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Onaylama başarısız: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectPlan = async () => {
    if (!selectedPlan) return;
    
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      await axios.put(`${API}/api/overtime-draft-plans/${selectedPlan.id}/reject?rejected_by=${auth.personnelId}&rejected_by_name=${encodeURIComponent(auth.personnelName || '')}&rejection_reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Plan reddedildi');
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchDraftPlans();
    } catch (error) {
      toast.error('Reddetme başarısız: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Silme onay diyalogunu aç
  const openDeleteConfirm = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  // Silme işlemini gerçekleştir
  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    try {
      await axios.delete(`${API}/api/overtime-draft-plans/${planToDelete.id}`);
      toast.success('Plan silindi');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      fetchDraftPlans();
    } catch (error) {
      toast.error('Plan silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const addParkAssignment = () => {
    setAssignments([...assignments, { park_id: '', park_name: '', personnel: [] }]);
  };

  // Yeni park eklendiğinde atama listesini aşağı kaydır ki "Park seçin" satırı görünsün
  useEffect(() => {
    if (assignments.length > prevAssignmentsLengthRef.current && assignmentsScrollRef.current) {
      prevAssignmentsLengthRef.current = assignments.length;
      requestAnimationFrame(() => {
        const el = assignmentsScrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    } else {
      prevAssignmentsLengthRef.current = assignments.length;
    }
  }, [assignments.length]);

  const updateAssignment = (index, field, value) => {
    const updated = [...assignments];
    updated[index][field] = value;
    if (field === 'park_id') {
      const park = parks.find(p => p.id === value);
      updated[index].park_name = park?.name || '';
    }
    setAssignments(updated);
  };

  const removeAssignment = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const togglePersonnelInAssignment = (assignmentIndex, person) => {
    const updated = [...assignments];
    const currentPersonnel = updated[assignmentIndex].personnel || [];
    const existingIndex = currentPersonnel.findIndex(p => p.personnel_id === person.id);
    
    if (existingIndex >= 0) {
      updated[assignmentIndex].personnel = currentPersonnel.filter(p => p.personnel_id !== person.id);
    } else {
      updated[assignmentIndex].personnel = [...currentPersonnel, {
        personnel_id: person.id,
        personnel_name: person.name,
        personnel_role: person.role,
        shift_type: 'full_day',
        hours_worked: 8
      }];
    }
    setAssignments(updated);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'taslak': return <Badge className="bg-gray-500">Taslak</Badge>;
      case 'onay_bekliyor': return <Badge className="bg-yellow-500">Bölge Müh. Onayı Bekliyor</Badge>;
      case 'bolge_muhendisi_onayladi': return <Badge className="bg-orange-500">Müd. Yrd. Onayı Bekliyor</Badge>;
      case 'mudur_yardimcisi_onayladi': return <Badge className="bg-blue-500">Müdür Onayı Bekliyor</Badge>;
      case 'onaylandi': return <Badge className="bg-green-500">Onaylandı</Badge>;
      case 'reddedildi': return <Badge className="bg-red-500">Reddedildi</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">📝 Mesai Taslak Planları</h3>
          <p className="text-sm text-gray-500">Park ve personel ataması yapın, onaya gönderin</p>
        </div>
        {canCreatePlan && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Yeni Plan Oluştur
          </Button>
        )}
      </div>

      {/* Durum Bazlı Gruplar */}
      <div className="space-y-6">
        {/* Tüm Onay Bekleyenler (tüm ara durumlar dahil) */}
        {draftPlans.filter(p => ['onay_bekliyor', 'bolge_muhendisi_onayladi', 'mudur_yardimcisi_onayladi'].includes(p.status)).length > 0 && (
          <div>
            <h4 className="font-semibold text-yellow-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Onay Sürecindeki Planlar ({draftPlans.filter(p => ['onay_bekliyor', 'bolge_muhendisi_onayladi', 'mudur_yardimcisi_onayladi'].includes(p.status)).length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {draftPlans.filter(p => ['onay_bekliyor', 'bolge_muhendisi_onayladi', 'mudur_yardimcisi_onayladi'].includes(p.status)).map(plan => (
                <Card key={plan.id} className={`border-2 ${
                  plan.status === 'onay_bekliyor' ? 'border-yellow-300 bg-yellow-50' :
                  plan.status === 'bolge_muhendisi_onayladi' ? 'border-orange-300 bg-orange-50' :
                  'border-blue-300 bg-blue-50'
                }`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h5 className="font-semibold">{plan.title}</h5>
                        <p className="text-sm text-gray-600">
                          {format(parseISO(plan.target_date), 'd MMMM yyyy, EEEE', { locale: tr })}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Oluşturan: {plan.created_by_name}
                        </p>
                      </div>
                      {getStatusBadge(plan.status)}
                    </div>
                    
                    <div className="text-sm mt-2">
                      <strong>{plan.assignments?.length || 0}</strong> park, 
                      <strong> {plan.assignments?.reduce((sum, a) => sum + (a.personnel?.length || 0), 0)}</strong> personel
                    </div>
                    
                    {/* Sıradaki onaylayıcı bilgisi */}
                    {plan.current_approver_name && !['onaylandi', 'reddedildi'].includes(plan.status) && (
                      <div className="mt-1 text-xs text-blue-600">
                        ➜ Sıradaki onay: {plan.current_approver_name}
                      </div>
                    )}
                    
                    {/* Değişiklik geçmişi varsa göster */}
                    {plan.change_history?.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500 bg-white rounded p-2 max-h-16 overflow-y-auto">
                        📝 Son değişiklik: {plan.change_history[plan.change_history.length - 1]?.description?.substring(0, 50)}...
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewPlan(plan)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detay / Düzenle
                      </Button>
                      {/* Onay/İptal butonu - YALNIZCA sıradaki makama (veya nihai
                          otorite müdüre) gösterilir. Sıra, backend'in dinamik
                          hiyerarşiden belirlediği plan.current_approver_role ile
                          eşleşir; böylece ör. müdür yardımcısı, sıra bölge
                          mühendisindeyken "Onayla" görmez (backend de engeller). */}
                      {(() => {
                        const isPending = !['taslak', 'onaylandi', 'reddedildi'].includes(plan.status);
                        if (!isPending) return false;
                        if (isMudur) return true; // müdür her aşamada nihai onay verebilir
                        const expected = plan.current_approver_role;
                        if (expected) {
                          return (expected === 'bolge_muhendisi' && isBolgeMuhendisi) ||
                                 (expected === 'mudur_yardimcisi' && isMudurYardimcisi);
                        }
                        // Eski planlar (current_approver_role alanı yok) → rol+durum fallback
                        return (plan.status === 'onay_bekliyor' && isBolgeMuhendisi) ||
                               (['onay_bekliyor', 'bolge_muhendisi_onayladi'].includes(plan.status) && isMudurYardimcisi);
                      })() && (
                        <>
                          <Button
                            size="sm"
                            data-testid={`overtime-approve-btn-${plan.id}`}
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprovePlan(plan.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {isMudurYardimcisi || isMudur ? 'Onayla (Son)' : 'Onayla'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-orange-600 border-orange-400"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setShowRejectDialog(true);
                            }}
                          >
                            İptal
                          </Button>
                        </>
                      )}
                      {/* Müdür/Müdür Yardımcısı Silme Butonu */}
                      {canDeletePlan && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openDeleteConfirm(plan)}
                          title="Planı sil"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Taslaklar */}
        {draftPlans.filter(p => p.status === 'taslak').length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-700 mb-3">Taslak Planlar</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {draftPlans.filter(p => p.status === 'taslak').map(plan => (
                <Card key={plan.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h5 className="font-semibold">{plan.title}</h5>
                        <p className="text-sm text-gray-600">
                          {format(parseISO(plan.target_date), 'd MMMM', { locale: tr })}
                        </p>
                      </div>
                      {getStatusBadge(plan.status)}
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewPlan(plan)}
                      >
                        Düzenle
                      </Button>
                      {/* Müdür/Müdür Yardımcısı direkt onaylayabilir */}
                      {['mudur', 'mudur_yardimcisi'].includes(userRole) ? (
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleDirectApprove(plan.id)}
                        >
                          Onayla
                        </Button>
                      ) : (
                        <Button 
                          size="sm"
                          onClick={() => handleSubmitForApproval(plan.id)}
                        >
                          Onaya Gönder
                        </Button>
                      )}
                      {canDeletePlan && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openDeleteConfirm(plan)}
                          title="Planı sil"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Onaylananlar */}
        {draftPlans.filter(p => p.status === 'onaylandi').length > 0 && (
          <div>
            <h4 className="font-semibold text-green-700 mb-3">✅ Onaylanan Planlar</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {draftPlans.filter(p => p.status === 'onaylandi').slice(0, 6).map(plan => (
                <Card 
                  key={plan.id} 
                  className="bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => handleViewPlan(plan)}
                >
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{plan.title}</p>
                        <p className="text-xs text-gray-500">
                          {format(parseISO(plan.target_date), 'd MMM', { locale: tr })} • 
                          {plan.assignments?.length || 0} park, {plan.assignments?.reduce((sum, a) => sum + (a.personnel?.length || 0), 0)} kişi
                        </p>
                        <p className="text-xs text-green-600">
                          Onaylayan: {plan.final_approved_by_name || plan.approved_by_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-green-500" />
                        {canDeletePlan && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteConfirm(plan);
                            }}
                            title="Planı sil"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Reddedilenler */}
        {draftPlans.filter(p => p.status === 'reddedildi').length > 0 && (
          <div>
            <h4 className="font-semibold text-red-700 mb-3">❌ Reddedilen Planlar</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {draftPlans.filter(p => p.status === 'reddedildi').slice(0, 3).map(plan => (
                <Card 
                  key={plan.id} 
                  className="bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => handleViewPlan(plan)}
                >
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{plan.title}</p>
                        <p className="text-xs text-gray-500">
                          {format(parseISO(plan.target_date), 'd MMM', { locale: tr })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-red-500" />
                        {canDeletePlan && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteConfirm(plan);
                            }}
                            title="Planı sil"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {draftPlans.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center text-gray-500">
              <CalendarClock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Henüz taslak plan bulunmuyor</p>
              {canCreatePlan && (
                <Button 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Planı Oluştur
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Plan Oluşturma Dialog */}
      <DraggableModal
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        title="Yeni Mesai Planı Oluştur"
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Plan Başlığı *</Label>
              <Input 
                value={planTitle}
                onChange={(e) => setPlanTitle(e.target.value)}
                placeholder="Örn: 11 Ocak Cumartesi Mesai Planı"
              />
            </div>
            <div className="space-y-2">
              <DatePicker
                label="Hedef Tarih"
                required
                value={targetDate}
                onChange={setTargetDate}
                highlightHolidays
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gün Türü</Label>
              <SearchableSelect
                value={dayType}
                onChange={setDayType}
                showAllOption={false}
                placeholder="Gün türü seçin"
                searchPlaceholder="Gün türü ara..."
                options={[
                  { id: "cumartesi", name: "Cumartesi" },
                  { id: "pazar", name: "Pazar" },
                  { id: "resmi_tatil", name: "Resmi Tatil" },
                  { id: "idari_tatil", name: "İdari Tatil" },
                ]}
              />
            </div>
          </div>

          {/* Anketten geldiyse bilgi göster, manuel ise seçim yap */}
          {surveyResponses.length > 0 ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Mesai anketinden {surveyResponses.length} kişi bu tarih için uygun
                  </p>
                  <p className="text-xs text-green-600">
                    Aşağıda yeşil renkle işaretli personeller ankete "evet" diyen kişilerdir.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">
                Bir mesai anketi ile ilişkilendirmek ister misiniz? (Opsiyonel)
              </Label>
              <SearchableSelect
                value={selectedSurveyId}
                onChange={(value) => {
                  setSelectedSurveyId(value);
                  if (value && value !== 'none' && targetDate) {
                    fetchSurveyResponsesForPlan(value, targetDate);
                  } else {
                    setSurveyResponses([]);
                  }
                }}
                allLabel="Anketsiz devam et"
                allValue="none"
                searchPlaceholder="Anket ara..."
                options={surveys.map(survey => ({ id: survey.id, name: survey.title }))}
              />
            </div>
          )}

          {/* Park Atamaları */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Park ve Personel Atamaları *</Label>
              <Button size="sm" variant="outline" onClick={addParkAssignment}>
                <Plus className="h-4 w-4 mr-1" />
                Park Ekle
              </Button>
            </div>
            
            <div ref={assignmentsScrollRef} className="space-y-3 max-h-64 overflow-y-auto">
              {assignments.map((assignment, index) => (
                <div key={index} className="p-3 border rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <SearchableSelect
                      value={assignment.park_id}
                      onChange={(value) => updateAssignment(index, 'park_id', value)}
                      showAllOption={false}
                      placeholder="Park seçin..."
                      searchPlaceholder="Park ara..."
                      className="flex-1"
                      options={getFilteredParks().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr')).map(park => ({ id: park.id, name: park.name }))}
                    />
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => removeAssignment(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  
                  {assignment.park_id && (
                    <div className="mt-2">
                      <Label className="text-xs">Personel Seç ({assignment.personnel?.length || 0} seçili)</Label>
                      
                      {/* Anketten gelen uygun personel varsa önce onları göster */}
                      {surveyResponses.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs text-green-600 font-medium">🟢 Mesaiye Gelmek İsteyenler ({surveyResponses.length})</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {surveyResponses
                              .filter(person => getFilteredPersonnel().some(fp => fp.id === person.id)) // Sadece yetkili personel
                              .map(person => (
                              <Badge 
                                key={person.id}
                                variant={assignment.personnel?.some(p => p.personnel_id === person.id) ? 'default' : 'outline'}
                                className={`cursor-pointer ${assignment.personnel?.some(p => p.personnel_id === person.id) 
                                  ? 'bg-green-600 hover:bg-green-700' 
                                  : 'border-green-500 text-green-700 hover:bg-green-50'}`}
                                onClick={() => {
                                  const fullPerson = personnel.find(p => p.id === person.id) || person;
                                  togglePersonnelInAssignment(index, fullPerson);
                                }}
                              >
                                ✓ {person.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Diğer tüm personel - sadece filtrelenmiş, alfabetik sıralı */}
                      <div>
                        {surveyResponses.length > 0 && (
                          <span className="text-xs text-gray-500">⚪ Diğer Personel</span>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1 max-h-24 overflow-y-auto">
                          {getFilteredPersonnel()
                            .filter(p => !surveyResponses.some(sr => sr.id === p.id)) // Uygun olanlari gösterme
                            .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr')) // Alfabetik sırala
                            .map(person => (
                              <Badge 
                                key={person.id}
                                variant={assignment.personnel?.some(p => p.personnel_id === person.id) ? 'default' : 'outline'}
                                className="cursor-pointer"
                                onClick={() => togglePersonnelInAssignment(index, person)}
                              >
                                {person.name}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {assignments.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Henüz park ataması yapılmadı. Aşağıdaki &quot;Park Ekle&quot; butonuna tıklayın.
                </p>
              )}
            </div>
          </div>

          {/* Özet bilgisi */}
          {assignments.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-800">
                📋 Plan Özeti: {assignments.length} park, {assignments.reduce((sum, a) => sum + (a.personnel?.length || 0), 0)} personel atandı
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
              İptal
            </Button>
            <Button 
              onClick={() => {
                if (!planTitle.trim() || !targetDate || assignments.length === 0) {
                  toast.error('Lütfen plan başlığı, tarih ve en az bir park ataması yapın');
                  return;
                }
                setShowConfirmSaveDialog(true);
              }} 
              disabled={loading || assignments.length === 0} 
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Taslak Olarak Kaydet
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* Kaydetme Onay Dialogu */}
      <DraggableModal
        isOpen={showConfirmSaveDialog}
        onClose={() => setShowConfirmSaveDialog(false)}
        title="Planı Kaydet"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">{planTitle}</h4>
            <p className="text-sm text-gray-600">📅 Tarih: {targetDate && format(parseISO(targetDate), 'd MMMM yyyy, EEEE', { locale: tr })}</p>
            <p className="text-sm text-gray-600">🏞️ Park: {assignments.length} adet</p>
            <p className="text-sm text-gray-600">👥 Personel: {assignments.reduce((sum, a) => sum + (a.personnel?.length || 0), 0)} kişi</p>

            {assignments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Detaylar</p>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {assignments.map((assignment, idx) => (
                    <div key={`${assignment.park_id || assignment.park_name || 'park'}-${idx}`} className="bg-white border rounded-md p-2 text-sm">
                      <p className="text-gray-700">
                        <span className="font-medium">📅 Gün:</span>{' '}
                        {targetDate ? format(parseISO(targetDate), 'd MMMM yyyy, EEEE', { locale: tr }) : '-'}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-medium">🏞️ Park:</span>{' '}
                        {assignment.park_name || 'Seçilmedi'}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-medium">👥 Personel:</span>{' '}
                        {(assignment.personnel && assignment.personnel.length > 0)
                          ? assignment.personnel.map((p) => p.personnel_name).join(', ')
                          : 'Atama yok'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <p className="text-sm text-gray-600">
            Bu planı taslak olarak kaydetmek istediğinize emin misiniz?
          </p>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowConfirmSaveDialog(false)} className="flex-1">
              Geri Dön
            </Button>
            <Button 
              onClick={() => {
                setShowConfirmSaveDialog(false);
                handleCreatePlan();
              }} 
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Kaydediliyor...' : 'Evet, Kaydet'}
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* İptal Dialog */}
      <DraggableModal
        isOpen={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        title="Planı İptal Et"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>{selectedPlan?.title}</strong> planını iptal etmek istediğinize emin misiniz?
          </p>
          
          <div className="space-y-2">
            <Label>İptal Nedeni (Opsiyonel)</Label>
            <Textarea 
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="İptal nedenini yazın..."
              rows={3}
            />
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1">
              Vazgeç
            </Button>
            <Button 
              onClick={handleRejectPlan} 
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              İptal Et
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* Plan Detay ve Düzenleme Dialog */}
      <DraggableModal
        isOpen={showDetailDialog}
        onClose={() => {
          setShowDetailDialog(false);
          setEditingPlan(null);
        }}
        title={`Plan Detayı: ${selectedPlan?.title || ''}`}
        maxWidth="max-w-3xl"
      >
        {selectedPlan && (
          <PlanDetailContent 
            plan={selectedPlan}
            userRole={userRole}
            personnel={personnel}
            parks={parks}
            canApprovePlan={canApprovePlan}
            onClose={() => setShowDetailDialog(false)}
            onUpdate={async (updatedAssignments, changeDescription) => {
              try {
                const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
                await axios.put(
                  `${API}/api/overtime-draft-plans/${selectedPlan.id}?updated_by=${auth.personnelId}&updated_by_name=${encodeURIComponent(auth.personnelName || '')}&updated_by_role=${auth.userRole}`,
                  { 
                    assignments: updatedAssignments,
                    change_description: changeDescription
                  }
                );
                toast.success('Plan güncellendi');
                fetchDraftPlans();
                // Güncel planı al
                const response = await axios.get(`${API}/api/overtime-draft-plans/${selectedPlan.id}`);
                setSelectedPlan(response.data);
              } catch (error) {
                toast.error('Plan güncellenemedi: ' + (error.response?.data?.detail || error.message));
              }
            }}
            onApprove={() => handleApprovePlan(selectedPlan.id)}
            onReject={() => {
              setShowDetailDialog(false);
              setShowRejectDialog(true);
            }}
            getFilteredParks={getFilteredParks}
            getFilteredPersonnel={getFilteredPersonnel}
          />
        )}
      </DraggableModal>

      {/* Plan Silme Onay Diyalogu */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Planı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>&quot;{planToDelete?.title}&quot;</strong> taslak planını silmek istediğinize emin misiniz?
              <br /><br />
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteConfirm(false);
              setPlanToDelete(null);
            }}>
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePlan}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaslakPlanlarSection;
